import * as React from 'react';
import {useEffect, useState} from 'react';
import {FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl} from 'react-intl';
import * as Yup from "yup";
import {Form, Formik, FormikProps} from "formik";
import {RouteComponentProps, withRouter} from "react-router";
import viaForSchools from "../../assets/viaForSchools.svg";
import {Button} from "@material-ui/core";
import {FormContainer, PageContainer, RowContainer} from '../common/styles';
import {CognitoUser} from "amazon-cognito-identity-js";
import * as H from "history";
import {ToastType} from "../../stores/ui-state.store";
import ToastSnackBar from "../common/toastSnackBar";
import viaLogo from "../../assets/viaLogo.svg";
import {SMS_MFA} from "../../constants/constants";
import {Link} from "react-router-dom";
import handleRedirect from '../../utils/redirectHelper';
import {logEvent, MParticleEventName} from "../../utils/m-particle-utils";
import AuthenticationCodeField from "../common/authenticationCodeField";


enum LoginFormFields {
    Totp = 'Totp',
}

interface OwnProps {
    user: CognitoUser | any;
    authClient: any;
    isViaForSchools: boolean;
    queryParams: string;
}


interface FormValues {
    [LoginFormFields.Totp]: string;
}

const getValidationSchema = (intl: InjectedIntl) => Yup.object().shape({
    [LoginFormFields.Totp]: Yup.string().matches(/^[0-9]{6}$/, intl.messages['login.totp.code-6-digits'])
        .required(intl.messages['login.totp.required']),
});

const initialValues: FormValues = {
    [LoginFormFields.Totp]: '',
};

type Props = FormikProps<FormValues> & RouteComponentProps & OwnProps & InjectedIntlProps;
const verifyTOTP = async (totpCode: string, user: CognitoUser | any, history: H.History,
                          setSubmitting: (isSubmitting: boolean) => void,
                          setShowToast: (isSubmitting: boolean) => void,
                          setMessage: (isSubmitting: string) => void,
                          setToastType: (toastType: ToastType) => void,
                          intl: any, authClient: any, queryParams: string, mfaType: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | null,) => {
    authClient.confirmSignIn(user, totpCode, mfaType)
        .then(() => {
            authClient.currentSession()
                .then(() => {
                    logEvent(MParticleEventName.LOGIN_SUCCESS, MfaValidation.displayName,
                        {username: user.getUsername().toLowerCase(), mfaType: mfaType,});
                    handleRedirect(history, queryParams);
            });
        })
        .catch((error: any) => {
            setToastType(ToastType.ERROR);
            logEvent(MParticleEventName.LOGIN_FAILURE, MfaValidation.displayName,
                {username: user.getUsername().toLowerCase(), errorCode: error.code, errorMessage: error.message,});
            if (error.code === "CodeMismatchException") {
                setMessage(intl.messages['login.totp.code-not-right']);
            } else if (error.code === "NotAuthorizedException") {
                if (error.message === 'Password attempts exceeded') {
                    setMessage(intl.messages['login.via.password-attempts-exceed']);
                } else{
                    setMessage(intl.messages['login.totp.session-is-ended']);
                }
            } else{
                setMessage(intl.messages['login.via.general-error-message']);
            }
            setShowToast(true);
            setSubmitting(false);
        });

};

const MfaValidation: React.FC<Props> = (props) => {
    const { user, history, location, intl, authClient, isViaForSchools, queryParams } = props;
    const [showToast, setShowToast] = useState(false);
    const [message, setMessage] = useState('');
    const [toastType, setToastType] = useState(ToastType.SUCCESS);
    if (!user.user) {
        history.push('/login');
    }
    // @ts-ignore
    const mfaMethod = location.state && location.state.challenge;
    // @ts-ignore
    const phoneNumber = location.state && location.state.phoneNumber;
    const validationSchema = getValidationSchema(intl);

    // @ts-ignore
    const passwordWasSet = location.state && location.state.passwordWasSet;

    useEffect(() => {
        if(passwordWasSet)
        {
            setMessage(intl.messages["login.password-set-message"]);
            setToastType(ToastType.SUCCESS);
            setShowToast(true);
        }
    }, [passwordWasSet, intl.messages]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={ async (fields, {setSubmitting}) => {
                const totp = fields[LoginFormFields.Totp];
                await verifyTOTP(totp, user.user, history,
                    setSubmitting,
                    setShowToast, setMessage, setToastType, intl, authClient, queryParams, mfaMethod);
                setSubmitting(false);
            }}
            render={() => (
                <PageContainer>
                    <FormContainer>
                        <RowContainer style={{backgroundColor: "#F3F5F8", margin: 0}}>
                            <div style={{padding: "20px", margin: "auto"}}>
                                { isViaForSchools ? <img src={viaForSchools} alt=""/> : <img src={viaLogo} alt=""/>}
                            </div>
                        </RowContainer>
                        <Form style={{ display: 'flex', flexDirection: 'column' }}>
                            {mfaMethod === SMS_MFA ?
                                <React.Fragment>
                                    <RowContainer style={{padding: "30px 60px 0px", flexDirection: "column", textAlign: "center"}}>
                                    <span style={{margin: "auto", color: "#555555"}}>
                                        <FormattedMessage id='login.via.mfa-validation.sms.sms-sent' values={{phoneNumber: `${phoneNumber}`}}/>
                                    </span>
                                    </RowContainer>
                                    <RowContainer style={{flexDirection: "column", textAlign: "center"}}>
                                    <span style={{margin: "auto", color: "#555555"}}>
                                        <FormattedMessage id='login.via.mfa-validation.sms.instruction'/>
                                    </span>
                                    </RowContainer>
                                </React.Fragment> :
                                <RowContainer style={{padding: "15px 20px 0px", flexDirection: "column", width: "380px"}}>
                                <span style={{margin: "auto", padding: "10px", color: "#555555"}}>
                                    <FormattedMessage id='login.via.mfa-validation'/>
                                </span>
                                </RowContainer>}

                            <RowContainer style={{padding: "0px 60px 15px", flexDirection: "column"}}>
                                <AuthenticationCodeField name={LoginFormFields.Totp}
                                                         label={intl.messages['login.totp.field-name']}
                                                         id={"mfa-validation.totp-field"}
                                />
                                <Button type="submit" data-testid={`login-button`}
                                        style={{width: "100%", marginTop: "25px", height: "40px",
                                            backgroundColor: "#20B7E9", color: "white"}}>
                                    <FormattedMessage id='login.via.login-button' />
                                </Button>
                            </RowContainer>
                            { mfaMethod === SMS_MFA &&
                            <RowContainer style={{padding: "0px 50px 0px", flexDirection: "column", textAlign: "center"}}>
                                    <span style={{margin: "auto", padding: "10px", color: "#555555"}}>
                                        <FormattedMessage id='login.via.mfa-validation.sms-not-received'/>
                                        <span style={{marginRight: "5px", marginLeft: "5px"}}>
                                            <Link to={"/login"}>
                                                <FormattedMessage id='login.via.mfa-validation.sms-not-received.here'/>
                                            </Link>
                                         </span>
                                        <FormattedMessage id='login.via.mfa-validation.sms-not-received.again'/>
                                    </span>
                            </RowContainer>}
                            <span style={{marginBottom: "60px"}}/>
                        </Form>
                    </FormContainer>
                    <ToastSnackBar show={showToast} message={message}
                                   toastType={toastType}
                                   onClose={() => {setShowToast(false); }}/>
                </PageContainer>
            )} />
    );
};
MfaValidation.displayName = 'MfaValidation';
export default withRouter(injectIntl(MfaValidation));
